/**
 * PlusMenu — the "+" button that opens a dropdown of available saved filter sets.
 * Groups into Shared and Personal sections.
 * Also shows a "Save filter set" option to create a new one.
 */

import React, { useCallback, useState } from 'react';
import { Button, Divider, Dropdown, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import FilterSetRow from './FilterSetRow';
import type {
  AvailableFilterSetsResponse,
  SavedFilterSet,
} from '@/pages/Shipments/ControlTower/types';
import styles from '../TabStrip.module.scss';
import { HeaderAddIcon } from '@/assets/icons/icons';

const { Text } = Typography;

interface Props {
  onFetchAvailable: () => Promise<AvailableFilterSetsResponse>;
  onSelectFilterSet: (filterSetId: string) => Promise<void>;
  onOpenCreateModal: () => void;
  onOpenEditModal: (filterSet: SavedFilterSet) => void;
  tableView?: boolean;
}

const PlusMenu: React.FC<Props> = ({
  onFetchAvailable,
  onSelectFilterSet,
  onOpenEditModal,
  tableView,
}) => {
  const { t } = useTranslation('shipments');
  const [loading, setLoading] = useState(false);
  const [available, setAvailable] = useState<AvailableFilterSetsResponse>({
    shared: [],
    personal: [],
  });
  const [open, setOpen] = useState(false);

  const handleOpenChange = useCallback(
    async (nextOpen: boolean) => {
      setOpen(nextOpen);

      if (nextOpen) {
        setLoading(true);
        try {
          const data = await onFetchAvailable();
          setAvailable(data);
        } finally {
          setLoading(false);
        }
      }
    },
    [onFetchAvailable]
  );

  const handleSelect = async (filterSetId: string) => {
    setOpen(false);
    await onSelectFilterSet(filterSetId);
  };

  const handleEdit = (filterSet: SavedFilterSet) => {
    setOpen(false);
    onOpenEditModal(filterSet);
  };

  const hasAny = available.shared.length > 0 || available.personal.length > 0;

  const dropdownContent = (
    <div data-testid='plus-menu-dropdown' className={styles.plusMenuDropdown}>
      {loading ? (
        <div className={styles.plusMenuLoading}>
          <Spin size='small' />
        </div>
      ) : !hasAny ? (
        <div className={styles.plusMenuEmpty}>
          <Text type='secondary'>{t('tabs.noAvailableFilterSets')}</Text>
        </div>
      ) : (
        <>
          {available.shared.length > 0 && (
            <>
              <div className={styles.plusMenuSectionHeader}>
                <Text className={styles.plusMenuSectionLabel}>
                  {t('tabs.sharedSection')}
                </Text>
              </div>

              {available.shared.map((fs) => (
                <FilterSetRow
                  key={fs.id}
                  filterSet={fs}
                  onClick={handleSelect}
                  onEdit={handleEdit}
                />
              ))}
            </>
          )}

          {available.personal.length > 0 && (
            <>
              {available.shared.length > 0 && (
                <Divider className={styles.plusMenuDivider} />
              )}

              <div className={styles.plusMenuSectionHeader}>
                <Text className={styles.plusMenuSectionLabel}>
                  {t('tabs.personalSection')}
                </Text>
              </div>

              {available.personal.map((fs) => (
                <FilterSetRow
                  key={fs.id}
                  filterSet={fs}
                  onClick={handleSelect}
                  onEdit={handleEdit}
                />
              ))}
            </>
          )}
        </>
      )}

      {/* <Divider className={styles.plusMenuDivider} /> */}

      {/* <div
        data-testid='plus-menu-save-filter-set'
        onClick={() => {
          setOpen(false);
          onOpenCreateModal();
        }}
        className={styles.plusMenuSaveOption}
      >
        + {t('tabs.saveFilterSet')}
      </div> */}
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={handleOpenChange}
      dropdownRender={() => dropdownContent}
      trigger={['click']}
      placement='bottomLeft'
    >
      <Button
        data-testid='plus-tab-button'
        type='text'
        icon={<HeaderAddIcon />}
        aria-label={t('tabs.addTab')}
        className={`${styles.plusButton} ${tableView && 'responsive-tab-absolute'} `}
      />
    </Dropdown>
  );
};

export default PlusMenu;
